import {
  useInstallIntegration,
  usePatchIntegration,
  useTestSlackChannel
} from "@/api/useIntegrations"
import { INTEGRATION_VENDORS } from "@/const/vendors-constants"

import type {
  SlackChannel,
  SlackInstallInput,
  SlackTestTarget,
  SlackUpdateInput
} from "@/api/useIntegrations.types"
import type { SlackAPIProps, SlackFormValues } from "./useSlackAPI.types"

const MESSAGE_PREFIX = "integrations.slack.messages"

export const toSlackInstallInput = (
  values: SlackFormValues
): SlackInstallInput => ({
  name: values.name?.trim() || "",
  url: values.url?.trim() || ""
})

export const toSlackUpdateInput = (
  channel: SlackChannel,
  values: SlackFormValues
): SlackUpdateInput => ({
  id: channel.id,
  name: values.name?.trim() || "",
  url: values.url?.trim() || undefined
})

// A URL being typed is tested as is; otherwise the saved channel is.
export const toSlackTestTarget = (
  channel: SlackChannel | undefined,
  url: string | undefined
): SlackTestTarget | null => {
  const typed = url?.trim()
  if (typed) return { url: typed }
  return channel ? { channelId: channel.id } : null
}

export const useSlackAPI = (props: SlackAPIProps) => {
  const { channel, t, message: messageApi } = props
  const vendor = INTEGRATION_VENDORS.slack
  const formId = channel?.id || `${vendor}-form`
  const installer = useInstallIntegration({ vendor })
  const updater = usePatchIntegration({ vendor })
  const tester = useTestSlackChannel()

  const onFinish = async (values: SlackFormValues) => {
    const prefix = `${MESSAGE_PREFIX}.${channel ? "update" : "add"}`
    try {
      if (channel) {
        await updater.mutateAsync({
          data: { slack: toSlackUpdateInput(channel, values) }
        })
      } else {
        await installer.mutateAsync({
          data: { slack: toSlackInstallInput(values) }
        })
      }
    } catch (error: any) {
      messageApi?.error(error?.data?.displayMessage || t(`${prefix}.failed`))
      return
    }

    messageApi?.success(t(`${prefix}.success`))
    if (channel) {
      props.form.setFieldValue("url", "")
      props.setIsDirty(false)
    } else if (props.onCreated)
      await props.onCreated(toSlackInstallInput(values).name)
    else props.backToIntegration(true)
  }

  const onTest = async (url?: string) => {
    const target = toSlackTestTarget(channel, url)
    if (!target) return
    try {
      await tester.mutateAsync(target)
    } catch (error: any) {
      messageApi?.error(
        error?.data?.displayMessage || t(`${MESSAGE_PREFIX}.test.failed`)
      )
      return
    }
    messageApi?.success(t(`${MESSAGE_PREFIX}.test.success`))
  }

  return {
    formId,
    onFinish,
    onTest,
    isTesting: tester.isPending,
    isPending: installer.isPending || updater.isPending
  }
}
